import 'react-phone-number-input/style.css'

import { CognitoUser } from 'amazon-cognito-identity-js'
import classNames from 'classnames'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import PhoneInput from 'react-phone-number-input'

import { AuthHeading } from '../../components/authentication/auth-heading/auth-heading'
import { Button } from '../../components/button'
import { Checkbox } from '../../components/checkbox'
import { FormGroup } from '../../components/form-group'
import { Input } from '../../components/input'
import {
    PasswordValidator,
    validatePassword,
} from '../../components/password-validator/password-validator'
import { Intent } from '../../config/intent'
import { authService } from '../../lib/auth-service'

export const TestId = {
    FORM: 'sign-up-form',
}

export const I18n = {
    HEADING: 'Create your Masa account',
    SUB_HEADING: 'View your finances, build credit, and access DeFi',
    EMAIL_LABEL: 'Email address*',
    PASSWORD_LABEL: 'Create password*',
    PHONE_LABEL: 'Phone number*',
    SUBMIT: 'Register account',
}

export interface CreateFormValues {
    email: string
    phone: string
    password: string
    terms: boolean
}

export interface SignUpFormProps {
    onComplete: (user: CognitoUser) => void
}

export function SignUpForm({ onComplete }: SignUpFormProps) {
    const [formError, setFormError] = React.useState('')
    const [loading, setLoading] = React.useState(false)
    const [show, setShow] = React.useState(false)

    const phoneContainerStyles = classNames(
        ' border border-gray rounded-md pl-4 h-12 flex items-center text-gray',
        'focus-within:outline-none focus-within:ring focus-within:ring-black focus-within:shadow-md',
    )

    const [formData, setFormData] = React.useState<CreateFormValues>({
        email: '',
        phone: '',
        password: '',
        terms: false,
    })

    const updateFormData = React.useCallback(
        newData => {
            setFormData({ ...formData, ...newData })
        },
        [formData],
    )

    const {
        formState: { errors },
        setError,
    } = useForm<CreateFormValues>()

    // handle server errors we can't do client side
    function handleError(err: any) {
        if (err.message === 'Invalid phone number format.') {
            setError('phone', { message: err.message })
        } else if (
            err.message === 'An account with the given email already exists.'
        ) {
            setError('email', { message: err.message })
        } else {
            setFormError(err.message)
        }
    }

    async function onSubmit(data: CreateFormValues) {
        if (!formData.terms) {
            return setError('terms', {
                message: 'You must agree to the terms and conditions',
            })
        }

        if (
            (formData.phone && formData.phone.length < 6) ||
            formData.phone === ''
        ) {
            return setError('phone', {
                message: 'Phone number must be valid',
            })
        }

        const passwordErrors = validatePassword(formData.password)
        if (passwordErrors.lowercase) {
            return setError('password', {
                message:
                    'Your password must have at least one lowercase character',
            })
        }

        if (passwordErrors.upperCase) {
            return setError('password', {
                message:
                    'Your password must have at least one uppercase character',
            })
        }

        if (passwordErrors.numbers) {
            return setError('password', {
                message: 'Your password must have at least one number',
            })
        }

        if (passwordErrors.characterRange) {
            return setError('password', {
                message: 'Your password must have at least 10 characters',
            })
        }

        setLoading(true)

        try {
            await authService.signUp({
                username: data.email,
                password: data.password,
                attributes: {
                    email: data.email,
                    phone_number: data.phone,
                },
            })

            const user = await authService.signIn({
                username: data.email,
                password: data.password,
            })

            setLoading(false)
            onComplete(user)
        } catch (err) {
            handleError(err)
            setLoading(false)
        }
    }

    return (
        <React.Fragment>
            <AuthHeading heading={I18n.HEADING} subHeading={I18n.SUB_HEADING} />
            <FormGroup
                label={I18n.EMAIL_LABEL}
                htmlFor='email'
                intent={errors.email?.message ? Intent.DANGER : Intent.DEFAULT}
                message={errors.email?.message}>
                <Input
                    id='email'
                    value={formData.email}
                    onChange={e => updateFormData({ email: e.target.value })}
                />
            </FormGroup>
            <FormGroup
                label={I18n.PHONE_LABEL}
                htmlFor='phone'
                intent={errors.phone?.message ? Intent.DANGER : Intent.DEFAULT}
                message={errors.phone?.message}>
                <PhoneInput
                    value={formData.phone}
                    onChange={(phone: string) =>
                        updateFormData({ phone: phone })
                    }
                    defaultCountry='US'
                    className={phoneContainerStyles}
                />
            </FormGroup>
            <FormGroup
                label={I18n.PASSWORD_LABEL}
                htmlFor='password'
                intent={
                    errors.password?.message ? Intent.DANGER : Intent.DEFAULT
                }
                message={errors.password?.message}>
                <Input
                    type={show ? 'text' : 'password'}
                    value={formData.password}
                    onChange={e => updateFormData({ password: e.target.value })}
                    rightElement={
                        <Button minimal={true} onClick={() => setShow(!show)}>
                            {show ? 'Hide' : 'Show'}
                        </Button>
                    }
                    id='password'
                />

                {errors.password && (
                    <>
                        <p className={'mt-4'}>Password must contain:</p>
                        <PasswordValidator str={formData.password} />
                    </>
                )}
            </FormGroup>
            {/* Note the Ts and Cs checkbox value isn't currently stored in the database but it does validate
            on the client side */}
            <FormGroup
                className='mb-10'
                intent={errors.terms?.message ? Intent.DANGER : Intent.DEFAULT}
                message={errors.terms?.message}>
                <Checkbox
                    value={formData.terms}
                    onChange={e => {
                        updateFormData({ terms: e.target.checked })
                    }}>
                    <React.Fragment>
                        <span>I agree to the</span>{' '}
                        <a
                            href='https://masa.finance/privacy-policy'
                            className='text-black'
                            target='_blank'
                            rel='noreferrer'>
                            terms and conditions
                        </a>
                    </React.Fragment>
                </Checkbox>
            </FormGroup>
            <Button
                fill={true}
                loading={loading}
                disabled={loading}
                onClick={() => onSubmit(formData)}
                type='submit'
                size='large'
                className='mb-4'>
                {I18n.SUBMIT}
            </Button>
            {formError && (
                <p className='text-center text-danger'>{formError}</p>
            )}
            <p className='mt-10 text-xs'>
                Your application, account, and device information and use
                patterns will be shared with Moon Laboratories Inc, financial
                partners, and service providers for application processing,
                identity and entity verification, fraud prevention, security, to
                tailor services to you, comply with our legal obligations, and
                communicate with you. Privacy Policy
            </p>
        </React.Fragment>
    )
}
